import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  Wallet, TrendingUp, Loader2, AlertTriangle, CheckCircle2, XCircle, ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "../api";
import { fmtVND, fmtCurrency, fmtDateTime } from "../lib/format";
import StatusChip from "../components/StatusChip";

export default function Admin() {
  const [recon, setRecon] = useState(null);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    const [r, p] = await Promise.all([api.reconciliation(), api.adminPending()]);
    setRecon(r);
    setPending(p);
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const approve = async (id) => {
    setBusy(id);
    try {
      await api.adminApprove(id);
      toast.success("Approved and submitted to rail");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approve failed");
    } finally {
      setBusy(null);
    }
  };

  const reject = async (id) => {
    setBusy(id);
    try {
      await api.adminReject(id, "Rejected by admin");
      toast("Rejected — reversal issued");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reject failed");
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="space-y-10">
      <header>
        <p className="text-xs uppercase tracking-widest text-orange-600 font-semibold">
          Ops Dashboard
        </p>
        <h1 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mt-1">
          Reconciliation
        </h1>
      </header>

      {/* KPI cards */}
      <section className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6" data-testid="kpi-cards">
        <KpiCard
          label="Total Ledger Balance"
          testId="kpi-ledger"
          value={recon ? fmtVND(recon.total_ledger_vnd) : "—"}
          sub={recon ? `Across ${recon.customer_count} customers` : ""}
          icon={<Wallet className="h-4 w-4 text-teal-700" />}
        />
        <KpiCard
          label="Completed Payouts"
          testId="kpi-completed"
          value={recon ? fmtVND(recon.completed_payouts.amount_vnd) : "—"}
          sub={recon ? `${recon.completed_payouts.count} payouts settled` : ""}
          icon={<CheckCircle2 className="h-4 w-4 text-emerald-600" />}
        />
        <KpiCard
          label="In-Flight Payouts"
          testId="kpi-inflight"
          value={recon ? fmtVND(recon.in_flight_payouts.amount_vnd) : "—"}
          sub={recon ? `${recon.in_flight_payouts.count} in progress` : ""}
          icon={<TrendingUp className="h-4 w-4 text-blue-600" />}
        />
      </section>

      {recon?.failed_payouts?.count > 0 && (
        <div className="flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4">
          <AlertTriangle className="h-5 w-5 text-amber-600" />
          <p className="text-sm text-amber-900">
            <span className="font-semibold">{recon.failed_payouts.count}</span> failed payouts
            ({fmtVND(recon.failed_payouts.amount_vnd)}) — reversed to customer wallets.
          </p>
        </div>
      )}

      {/* Approval Queue */}
      <section>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display text-xl font-semibold text-slate-900">
            Payout Approval Queue
          </h2>
          <Link
            to="/admin/payouts"
            data-testid="view-all-admin-payouts"
            className="text-sm font-medium text-teal-700 hover:text-teal-800 flex items-center gap-1"
          >
            All payouts <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          {loading && (
            <div className="p-8 text-center text-sm text-slate-500">
              <Loader2 className="h-4 w-4 animate-spin inline mr-2" /> Loading…
            </div>
          )}
          {!loading && pending.length === 0 && (
            <div className="p-8 text-center text-sm text-slate-500">
              No payouts awaiting approval. 🎉
            </div>
          )}
          <div className="divide-y divide-slate-100">
            {pending.map((p) => (
              <div
                key={p.id}
                data-testid={`approval-row-${p.id}`}
                className="flex flex-col sm:flex-row sm:items-center gap-3 px-5 py-4 hover:bg-slate-50 transition-colors"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-display text-lg font-bold text-slate-900 tabular-nums">
                      {fmtCurrency(p.net_amount, p.payout_currency)}
                    </span>
                    <span className="text-xs text-slate-400">
                      ({fmtVND(p.amount_vnd)} gross)
                    </span>
                    <StatusChip status={p.status} />
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-xs text-slate-500 flex-wrap">
                    <span>{p.destination_snapshot?.bank_name}</span>
                    <span>•</span>
                    <span>{p.destination_snapshot?.account_holder}</span>
                    <span>•</span>
                    <span>{fmtDateTime(p.created_at)}</span>
                  </div>
                </div>
                <div className="flex gap-2 shrink-0">
                  <button
                    data-testid={`approve-btn-${p.id}`}
                    onClick={() => approve(p.id)}
                    disabled={busy === p.id}
                    className="inline-flex items-center gap-1.5 rounded-full bg-teal-700 px-4 py-2 text-xs font-semibold text-white hover:bg-teal-800 disabled:opacity-50"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" /> Approve
                  </button>
                  <button
                    data-testid={`reject-btn-${p.id}`}
                    onClick={() => reject(p.id)}
                    disabled={busy === p.id}
                    className="inline-flex items-center gap-1.5 rounded-full border border-red-200 bg-white px-4 py-2 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-50"
                  >
                    <XCircle className="h-3.5 w-3.5" /> Reject
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

const KpiCard = ({ label, value, sub, icon, testId }) => (
  <div
    data-testid={testId}
    className="rounded-2xl border border-slate-200 bg-white p-6 hover:shadow-sm transition-shadow"
  >
    <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500 font-semibold">
      {icon}
      {label}
    </div>
    <div className="mt-3 kpi-num text-3xl sm:text-4xl text-slate-900 tabular-nums">
      {value}
    </div>
    {sub && <p className="mt-1.5 text-xs text-slate-500">{sub}</p>}
  </div>
);
